import { MagicPolygonHandler } from './MagicPolygonHandler';
import { ActiveTool } from 'src/components/viewers/SheetsViewerV2/enums/ActiveTool';
import { AggregatedView } from 'src/components/viewers/SheetsViewerV2/hooks/useSheetsAggregatedView';
import { SheetScaleType, SheetShapeDeepFragment } from 'src/gql/graphql';
import {
  CROSSHAIR_CURSOR,
  MEASUREMENT_TOOL_SNAPPING_ANGLE_STEP,
  MOVE_CURSOR,
  WAIT_CURSOR,
  VIEWER_TOP_OFFSET,
  InvalidShapeStyle,
  ValidShapeStyle,
  InvalidLineStyle,
  ValidLineStyle,
  VertexStyle,
  FirstVertexStyle,
  HoveredValidLineStyle,
} from 'src/components/viewers/SheetsViewerV2/SheetsViewerV2.config';
import {
  distanceBetweenInPx,
  getSnapPoint,
  getSnapPointByDistance,
} from 'src/components/viewers/SheetsViewerV2/utils/snapping';
import { ActiveSheetData } from 'src/components/viewers/SheetsViewerV2/hooks/useActiveSheet';
import { ISheetShape } from 'src/components/viewers/SheetsViewerV2/shapes/abstract/AbstractSheetShape';
import { mapPointsToPolylineShape } from 'src/components/viewers/SheetsViewerV2/mappers/mapPointsToPolylineShape';
import { mapPointsToSplitLineShape } from 'src/components/viewers/SheetsViewerV2/mappers/mapPointsToSplitLineShape';
import { mapPointsToPolygonShape } from 'src/components/viewers/SheetsViewerV2/mappers/mapPointsToPolygonShape';
import { PolylineShape } from 'src/components/viewers/SheetsViewerV2/shapes/PolylineShape';
import {
  angleBetweenTwoPoints,
  endPointByAngle,
  snapAngle,
} from 'src/components/viewers/SheetsViewerV2/utils/cartesian';
import {
  findPointLocations,
  PointLocation,
} from 'src/components/viewers/SheetsViewerV2/utils/drawing';
import Edit2D = Autodesk.Extensions.Edit2D;
import Viewer3D = Autodesk.Viewing.Viewer3D;
import GuiViewer3D = Autodesk.Viewing.GuiViewer3D;

export enum DrawingMode {
  Point = 'point',
  Polyline = 'Polyline',
  Rectangle = 'rectangle',
  Polygon = 'polygon',
  MagicPolygon = 'magic-polygon',
  Wedges = 'wedge',
  DrainLine = 'drain-line',
  RoofOutline = 'roof-outline',
}

export enum ActiveMode {
  Point = 'point',
  Edge = 'edge',
  Shape = 'shape',
}

type PointWithLocations = {
  point: THREE.Vector3;
  locations: PointLocation[];
};

// Screen-space vertex element that maintains consistent pixel size
class ScreenSpaceVertex {
  private element: HTMLDivElement;
  private viewer: Viewer3D | GuiViewer3D;
  private pointGetter: () => THREE.Vector3;
  private style: any;
  private isFirstVertex: boolean;

  constructor(
    viewer: Viewer3D | GuiViewer3D,
    pointGetter: () => THREE.Vector3,
    style: any,
    isFirstVertex = false
  ) {
    this.viewer = viewer;
    this.pointGetter = pointGetter;
    this.style = style;
    this.isFirstVertex = isFirstVertex;

    this.element = document.createElement('div');
    this.element.style.position = 'absolute';
    this.element.style.pointerEvents = 'none';
    this.element.style.zIndex = '1000';
    this.element.style.width = '8px';
    this.element.style.height = '8px';
    this.element.style.borderRadius = '50%';
    this.element.style.border = `${this.style.lineWidth}px solid ${this.style.lineColor}`;
    this.element.style.backgroundColor = this.style.fillColor;

    // Add to viewer container
    const container = this.viewer.container;
    if (container) {
      container.appendChild(this.element);
    }

    this.updatePosition();
  }

  private updatePosition() {
    // Get the current point position
    const point = this.pointGetter();

    // Convert world coordinates to screen coordinates
    const projected = Autodesk.Viewing.MeasureCommon.project(
      point,
      this.viewer
    );

    // Center the vertex on the point (vertex is 8px wide/high, so subtract 4)
    this.element.style.left = `${projected.x - 4}px`;
    this.element.style.top = `${projected.y - 4}px`;
  }

  public update() {
    this.updatePosition();
  }

  public remove() {
    if (this.element.parentNode) {
      this.element.parentNode.removeChild(this.element);
    }
  }
}

export class ShapesDrawingTool extends Autodesk.Viewing.ToolInterface {
  names = [ActiveTool.Drawing];
  shapes: ISheetShape[] = [];
  private screenSpaceVertices: ScreenSpaceVertex[] = [];
  private pointsArray: THREE.Vector3[][] = [];
  private pointsArrayIndex = 0;
  private cursorPoint: THREE.Vector3 | null = null;
  private hoveredPoint: PointWithLocations | null = null;
  private hoveredLine: THREE.Line3 | null = null;
  private hoveredLineShape: PolylineShape | null = null;
  private activePoints: PointWithLocations[] = [];
  private activePointsMode: ActiveMode | null = null;
  private activePointerInitPosition: THREE.Vector3 | null = null;
  private activePointerCurrentPosition: THREE.Vector3 | null = null;
  private magicPolygonHandler: MagicPolygonHandler = new MagicPolygonHandler();
  private _keyDownHandler: ((event: KeyboardEvent) => void) | null = null;
  private buttonDownShiftKey = false;
  private isMagicPolygonLoading = false;

  // Simple undo/redo history for the current drawing session
  private historyPast: {
    pointsArray: THREE.Vector3[][];
    pointsArrayIndex: number;
  }[] = [];
  private historyFuture: {
    pointsArray: THREE.Vector3[][];
    pointsArrayIndex: number;
  }[] = [];

  // Vertex selection state (geometric points, independent of shapes array indices)
  private selectedPoints: THREE.Vector3[] = [];

  // Box selection state for vertices (screen-space, relative to viewer container)
  private vertexBoxActive = false;
  private vertexBoxStart: { x: number; y: number } | null = null;
  private vertexBoxCurrent: { x: number; y: number } | null = null;
  private vertexBoxElement: HTMLDivElement | null = null;

  // Pointer handlers for vertex box selection (Ctrl/Cmd + drag)
  private _onVertexBoxPointerDown = (event: PointerEvent) => {
    // Only respond to left button with Ctrl/Cmd
    if (event.button !== 0 || (!event.ctrlKey && !event.metaKey)) {
      return;
    }

    const clientX = event.clientX;
    const clientY = event.clientY - VIEWER_TOP_OFFSET;

    this._startVertexBoxSelection(clientX, clientY);

    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
  };

  private _onVertexBoxPointerMove = (event: PointerEvent) => {
    if (!this.vertexBoxActive) {
      return;
    }

    const clientX = event.clientX;
    const clientY = event.clientY - VIEWER_TOP_OFFSET;

    this._updateVertexBoxSelection(clientX, clientY);

    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
  };

  private _onVertexBoxPointerUp = (event: PointerEvent) => {
    if (!this.vertexBoxActive) {
      return;
    }

    const clientX = event.clientX;
    const clientY = event.clientY - VIEWER_TOP_OFFSET;

    this._endVertexBoxSelection(clientX, clientY, event.shiftKey);

    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
  };

  constructor(
    private readonly viewer: AggregatedView['viewer'],
    private readonly snapper: Autodesk.Viewing.Extensions.Snapping.Snapper,
    private readonly edit2d: Edit2D,
    private readonly activeSheetData: ActiveSheetData,
    public readonly drawingMode: DrawingMode,
    public readonly onUpdate: (points: THREE.Vector3[][]) => void,
    private readonly initialPoints: THREE.Vector3[][] | null = null,
    private readonly calibration: SheetScaleType | null = null,
    private readonly renderedSheetShapes: Record<
      string,
      SheetShapeDeepFragment[]
    > | null = null
  ) {
    super();

    if (this.initialPoints) {
      this.pointsArray = this.initialPoints;
      this.pointsArrayIndex =
        this.drawingMode === DrawingMode.Polyline
          ? this.initialPoints.length - 1 // Continue drawing from the last point of the initial points
          : this.initialPoints.length;
      this.onUpdate([...this.pointsArray]);
    }

    delete (this as any).activate;
    delete (this as any).deactivate;
    delete (this as any).handleSingleClick;
    delete (this as any).handleMouseMove;
    delete (this as any).handleDoubleClick;
    delete (this as any).handleButtonDown;
    delete (this as any).handleButtonUp;
  }

  getCursor(): string {
    // Show loading cursor when waiting for magic polygon API response
    if (
      this.drawingMode === DrawingMode.MagicPolygon &&
      this.isMagicPolygonLoading
    ) {
      return WAIT_CURSOR;
    }
    if (this.activePoints.length > 0) return MOVE_CURSOR;
    if (this.hoveredPoint) return MOVE_CURSOR;
    if (this.hoveredLine || this.hoveredLineShape) return MOVE_CURSOR;
    return CROSSHAIR_CURSOR;
  }

  activate() {
    // Immediately draw any existing shapes when the tool is activated
    this.draw();

    // Set up camera change listener to update screen-space vertices
    this.viewer.addEventListener(
      Autodesk.Viewing.CAMERA_CHANGE_EVENT,
      this._onCameraChange
    );

    // Attach pointer listeners for vertex box selection (capture phase to beat viewer tools)
    const container = this.viewer.container as HTMLElement | null;
    if (container) {
      container.addEventListener(
        'pointerdown',
        this._onVertexBoxPointerDown,
        true
      );
      container.addEventListener(
        'pointermove',
        this._onVertexBoxPointerMove,
        true
      );
      container.addEventListener('pointerup', this._onVertexBoxPointerUp, true);
    }

    // Keyboard handlers for undo / redo (Ctrl/Cmd+Z, Ctrl/Cmd+Y)
    // eslint-disable-next-line complexity
    this._keyDownHandler = (event: KeyboardEvent) => {
      const isUndo =
        (event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'z';
      const isRedo =
        (event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'y';
      const isDelete = event.key === 'Delete' || event.key === 'Backspace';

      if (!isUndo && !isRedo && !isDelete) {
        return false;
      }

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();

      if (isUndo) {
        this._undo();
      } else if (isRedo) {
        this._redo();
      } else if (isDelete) {
        // Delete selected vertices (geometry change)
        if (this.selectedPoints.length > 0) {
          this._pushHistorySnapshot();
          const toRemove = [...this.selectedPoints];
          this._clearVertexSelection();
          toRemove.forEach((point) => this._removePoint(point));
          this.draw();
          this.onUpdate?.([...this.pointsArray]);
        }
      }

      return true;
    };

    // Use capture phase to ensure we handle the event before the viewer
    window.addEventListener('keydown', this._keyDownHandler, true);
  }

  deactivate() {
    this.pointsArray = [];
    this.pointsArrayIndex = 0;
    this.cursorPoint = null;
    this.hoveredPoint = null;
    this.hoveredLine = null;
    this.activePoints = [];
    this.activePointsMode = null;
    this.activePointerInitPosition = null;
    this.activePointerCurrentPosition = null;
    this.buttonDownShiftKey = false;
    this.clear();

    // Reset MagicPolygon handler to ensure proper cleanup
    this.magicPolygonHandler.reset();

    // Remove camera change listener
    this.viewer.removeEventListener(
      Autodesk.Viewing.CAMERA_CHANGE_EVENT,
      this._onCameraChange
    );

    // Remove vertex box pointer listeners
    const container = this.viewer.container as HTMLElement | null;
    if (container) {
      container.removeEventListener(
        'pointerdown',
        this._onVertexBoxPointerDown,
        true
      );
      container.removeEventListener(
        'pointermove',
        this._onVertexBoxPointerMove,
        true
      );
      container.removeEventListener(
        'pointerup',
        this._onVertexBoxPointerUp,
        true
      );
    }

    // Remove keyboard event listener
    if (this._keyDownHandler) {
      window.removeEventListener('keydown', this._keyDownHandler, true);
      this._keyDownHandler = null;
    }

    // Clear vertex selection and box state
    this.selectedPoints = [];
    if (this.vertexBoxElement && this.vertexBoxElement.parentNode) {
      this.vertexBoxElement.parentNode.removeChild(this.vertexBoxElement);
    }
    this.vertexBoxElement = null;
    this.vertexBoxActive = false;
    this.vertexBoxStart = null;
    this.vertexBoxCurrent = null;
  }

  private _onCameraChange = () => {
    // Update all screen-space vertices when camera changes
    this.screenSpaceVertices.forEach((vertex) => vertex.update());
  };

  private _handleMagicPolygonButtonDown(
    event: MouseEvent,
    button: number
  ): boolean {
    if (button === 0) {
      // Refresh hover state to ensure we have the latest hovered point/edge
      this._updateHoveredPointFromEvent(event);
      this._updateHoveredLineFromEvent(event);

      // Store shift key state for use in handleButtonUp
      this.buttonDownShiftKey = event.shiftKey;

      // Left click - handle normally
      this.activePointerInitPosition = this._getWorldPositionFromMouseEvent(
        event,
        false
      );
      this.activePointerCurrentPosition = this.activePointerInitPosition;

      // Check if we're clicking on a vertex or edge for editing
      // For MagicPolygon, we can safely set activePoints immediately.
      // Whether it's treated as a click or a drag is decided in handleButtonUp via the 'moved' flag.
      if (
        this.hoveredPoint &&
        this.pointsArray[this.pointsArrayIndex]?.length > 0
      ) {
        this.activePointsMode = ActiveMode.Point;
        this.activePoints = [this.hoveredPoint];
        return true; // Consume event to prevent viewer from handling it
      } else if (
        this.hoveredLine &&
        this.pointsArray[this.pointsArrayIndex]?.length > 0
      ) {
        this.activePointsMode = ActiveMode.Edge;
        this.activePoints = [
          {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            point: this.hoveredLine.start,
            locations: findPointLocations(
              this.pointsArray,
              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-ignore
              this.hoveredLine.start
            ),
          },
          {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            point: this.hoveredLine.end,
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            locations: findPointLocations(
              this.pointsArray,
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              (this.hoveredLine as any).end
            ),
          },
        ];
        return true; // Consume event to prevent viewer from handling it
      }

      // Not clicking on vertex/edge - allow normal click handling
      return true; // Consume event for MagicPolygon mode
    }
    // Right click - don't consume, allow panning
    return false;
  }

  handleButtonDown(event: MouseEvent, button: number): boolean {
    // For MagicPolygon, only handle left-click events, let right-click events pass through for panning
    if (this.drawingMode === DrawingMode.MagicPolygon) {
      return this._handleMagicPolygonButtonDown(event, button);
    }

    return this._handleRegularButtonDown(event, button);
  }

  private _clonePointsArray(points: THREE.Vector3[][]): THREE.Vector3[][] {
    return points.map((shape) => shape.map((p) => p.clone()));
  }

  private _pushHistorySnapshot(): void {
    this.historyPast.push({
      pointsArray: this._clonePointsArray(this.pointsArray),
      pointsArrayIndex: this.pointsArrayIndex,
    });
    this.historyFuture = [];
  }

  private _restoreSnapshot(snapshot: {
    pointsArray: THREE.Vector3[][];
    pointsArrayIndex: number;
  }): void {
    this.pointsArray = this._clonePointsArray(snapshot.pointsArray);
    this.pointsArrayIndex = snapshot.pointsArrayIndex;
    this.draw();
    this.onUpdate?.([...this.pointsArray]);
  }

  private _undo(): void {
    if (this.historyPast.length === 0) {
      return;
    }
    const previous = this.historyPast.pop() as {
      pointsArray: THREE.Vector3[][];
      pointsArrayIndex: number;
    };
    const current = {
      pointsArray: this._clonePointsArray(this.pointsArray),
      pointsArrayIndex: this.pointsArrayIndex,
    };
    this.historyFuture.unshift(current);
    this._restoreSnapshot(previous);
  }

  private _redo(): void {
    if (this.historyFuture.length === 0) {
      return;
    }
    const next = this.historyFuture.shift() as {
      pointsArray: THREE.Vector3[][];
      pointsArrayIndex: number;
    };
    const current = {
      pointsArray: this._clonePointsArray(this.pointsArray),
      pointsArrayIndex: this.pointsArrayIndex,
    };
    this.historyPast.push(current);
    this._restoreSnapshot(next);
  }

  private _startVertexBoxSelection(x: number, y: number): void {
    this.vertexBoxActive = true;
    this.vertexBoxStart = { x, y };
    this.vertexBoxCurrent = { x, y };

    const container = this.viewer.container as HTMLElement | null;
    if (!container) {
      return;
    }

    const box = document.createElement('div');
    box.style.position = 'absolute';
    box.style.zIndex = '2000';
    box.style.pointerEvents = 'none';
    box.style.border = '1px solid rgba(0, 136, 255, 0.8)';
    box.style.backgroundColor = 'rgba(0, 136, 255, 0.15)';

    container.appendChild(box);
    this.vertexBoxElement = box;
  }

  private _updateVertexBoxSelection(x: number, y: number): void {
    if (
      !this.vertexBoxActive ||
      !this.vertexBoxStart ||
      !this.vertexBoxElement
    ) {
      return;
    }

    this.vertexBoxCurrent = { x, y };

    const start = this.vertexBoxStart;
    const end = this.vertexBoxCurrent;

    const left = Math.min(start.x, end.x);
    const top = Math.min(start.y, end.y);
    const width = Math.abs(start.x - end.x);
    const height = Math.abs(start.y - end.y);

    this.vertexBoxElement.style.left = `${left}px`;
    this.vertexBoxElement.style.top = `${top}px`;
    this.vertexBoxElement.style.width = `${width}px`;
    this.vertexBoxElement.style.height = `${height}px`;
  }

  private _endVertexBoxSelection(
    x: number,
    y: number,
    additive: boolean
  ): void {
    if (!this.vertexBoxActive || !this.vertexBoxStart) {
      return;
    }

    this.vertexBoxCurrent = { x, y };

    const start = this.vertexBoxStart;
    const end = this.vertexBoxCurrent;

    const minX = Math.min(start.x, end.x);
    const maxX = Math.max(start.x, end.x);
    const minY = Math.min(start.y, end.y);
    const maxY = Math.max(start.y, end.y);

    // Ignore very small boxes (treat as click)
    if (Math.abs(maxX - minX) < 5 || Math.abs(maxY - minY) < 5) {
      this._clearVertexBoxVisuals();
      return;
    }

    const newlySelected: THREE.Vector3[] = [];

    // For each point in all shapes, project to screen space and test inside box
    this.pointsArray.forEach((shape) => {
      shape.forEach((point) => {
        const projected = Autodesk.Viewing.MeasureCommon.project(
          point,
          this.viewer
        );
        if (
          projected.x >= minX &&
          projected.x <= maxX &&
          projected.y >= minY &&
          projected.y <= maxY
        ) {
          newlySelected.push(point.clone());
        }
      });
    });

    if (!additive) {
      this.selectedPoints = newlySelected;
    } else {
      // Union with existing selection
      newlySelected.forEach((p) => {
        if (!this._isPointSelected(p)) {
          this.selectedPoints.push(p);
        }
      });
    }

    this._clearVertexBoxVisuals();
    this.draw();
  }

  private _clearVertexBoxVisuals(): void {
    this.vertexBoxActive = false;
    this.vertexBoxStart = null;
    this.vertexBoxCurrent = null;
    if (this.vertexBoxElement && this.vertexBoxElement.parentNode) {
      this.vertexBoxElement.parentNode.removeChild(this.vertexBoxElement);
    }
    this.vertexBoxElement = null;
  }

  private _isPointSelected(point: THREE.Vector3): boolean {
    return this.selectedPoints.some((sel) => sel.equals(point));
  }

  private _clearVertexSelection(): void {
    this.selectedPoints = [];
  }

  private _toggleVertexSelection(
    point: THREE.Vector3,
    additive: boolean
  ): void {
    if (!additive) {
      // Replace selection
      this.selectedPoints = [point.clone()];
      return;
    }

    const alreadySelected = this._isPointSelected(point);
    if (alreadySelected) {
      this.selectedPoints = this.selectedPoints.filter(
        (sel) => !sel.equals(point)
      );
    } else {
      this.selectedPoints.push(point.clone());
    }
  }

  private _handleRegularButtonDown(event: MouseEvent, button: number): boolean {
    this.activePointerInitPosition = this._getWorldPositionFromMouseEvent(
      event,
      false
    );
    this.activePointerCurrentPosition = this.activePointerInitPosition;

    if (this.hoveredPoint && button === 0) {
      this.activePointsMode = ActiveMode.Point;
      this.activePoints = [this.hoveredPoint];
      return true;
    } else if (this.hoveredLine && button === 0) {
      this.activePointsMode = ActiveMode.Edge;
      this.activePoints = [
        {
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          point: this.hoveredLine.start,
          locations: findPointLocations(
            this.pointsArray,
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            this.hoveredLine.start
          ),
        },
        {
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          point: this.hoveredLine.end,
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          locations: findPointLocations(this.pointsArray, this.hoveredLine.end),
        },
      ];
      return true;
    }

    return false;
  }

  private _handleMagicPolygonButtonUp(
    event: MouseEvent,
    moved: boolean
  ): boolean {
    if (moved) {
      // Snapshot before applying drag so the whole drag is a single undo step
      this._pushHistorySnapshot();
      this.pointsArray = this._mapPointsArrayAndActivePoints();
      this.magicPolygonHandler.markUserInteraction();
      this.onUpdate([...this.pointsArray]);
    } else if (
      this.activePointsMode === ActiveMode.Point &&
      this.pointsArray[this.pointsArrayIndex]?.length > 0
    ) {
      // No movement - update hover states and check if clicking on a vertex for selection
      this._updateHoveredPointFromEvent(event);
      if (this.hoveredPoint) {
        // No movement and clicking on a vertex - handle selection
        // Use the shift key state from buttonDown to ensure it's preserved
        const additive = this.buttonDownShiftKey;
        this._toggleVertexSelection(this.hoveredPoint.point, additive);
      }
    }
    // Clear shift key state
    this.buttonDownShiftKey = false;
    return true; // Always consume event in MagicPolygon mode
  }

  private _handleRegularButtonUp(
    event: MouseEvent,
    moved: boolean,
    hadActivePoints: boolean
  ): boolean {
    // CRITICAL: Capture the final pointsArray state BEFORE clearing active points
    // If we moved, pointsArray should already be updated with moved points
    // Create a deep clone for onUpdate to ensure the hook gets independent Vector3 objects
    const finalPointsArray = this.pointsArray.map((points) =>
      points.map((p) => p.clone())
    );

    // Clear active points state BEFORE draw/onUpdate
    // This ensures draw() uses the updated pointsArray directly, not _mapPointsArrayAndActivePoints()
    this.activePoints = [];
    this.activePointsMode = null;
    this.activePointerInitPosition = null;
    this.activePointerCurrentPosition = null;

    // CRITICAL: Update pointsArray with the final state to ensure draw() uses it
    // This is important because draw() calls _mapPointsArrayAndActivePoints() which returns pointsArray
    // when activePoints is cleared, so we need to ensure pointsArray has the updated values
    if (moved) {
      this.pointsArray = finalPointsArray.map((points) =>
        points.map((p) => p.clone())
      );
    }

    // Draw with the updated pointsArray
    // Since activePoints is cleared, _mapPointsArrayAndActivePoints() will return this.pointsArray directly
    this.draw();

    // CRITICAL: Always call onUpdate if we had active points (dragging) to ensure the hook gets the final state
    // This must happen AFTER updating pointsArray and clearing activePoints
    if (hadActivePoints) {
      // Use the cloned array to ensure the hook gets independent Vector3 objects
      this.onUpdate(finalPointsArray);
    }

    // Return true if we clicked on a vertex/edge (even without dragging) to prevent camera nudging
    // or if we actually dragged
    return moved || hadActivePoints;
  }

  private _computeMovedFlag(event: MouseEvent): {
    moved: boolean;
    hadActivePoints: boolean;
  } {
    const cursorPosition = this._getWorldPositionFromMouseEvent(event, false);
    const hadActivePoints = this.activePoints.length > 0;

    let moved = false;
    if (this.drawingMode === DrawingMode.MagicPolygon) {
      // For MagicPolygon, use a small *pixel* movement threshold to distinguish click vs drag
      if (hadActivePoints && cursorPosition && this.activePointerInitPosition) {
        const pxDistance =
          distanceBetweenInPx(
            this.viewer,
            cursorPosition,
            this.activePointerInitPosition
          ) ?? 0;
        moved = pxDistance > 3; // ~3px threshold for reliable small drags
      } else {
        moved = false;
      }
    } else {
      // For regular modes (Polygon, Rectangle, etc.), treat any mouse up with
      // active points as a movement commit to avoid snap-back.
      moved = hadActivePoints;
    }

    return { moved, hadActivePoints };
  }

  handleButtonUp(event: MouseEvent): boolean {
    // Finish vertex box selection, if active
    if (this.vertexBoxActive && this.vertexBoxStart) {
      const x = event.clientX;
      const y = event.clientY - VIEWER_TOP_OFFSET;
      this._endVertexBoxSelection(x, y, event.shiftKey);
      return true;
    }

    // For MagicPolygon, let right-click button up pass through so the viewer
    // can correctly stop panning. We only care about left-button for edits.
    if (this.drawingMode === DrawingMode.MagicPolygon && event.button === 2) {
      return false;
    }

    const { moved, hadActivePoints } = this._computeMovedFlag(event);

    // Handle MagicPolygon mode separately
    if (this.drawingMode === DrawingMode.MagicPolygon) {
      // Let _handleMagicPolygonButtonUp handle snapshot, geometry commit and onUpdate
      const result = this._handleMagicPolygonButtonUp(event, moved);
      // Clear dragging state
      this.activePoints = [];
      this.activePointsMode = null;
      this.activePointerInitPosition = null;
      this.activePointerCurrentPosition = null;
      this.draw();
      return result;
    }

    // For regular modes, update pointsArray if moved
    if (moved) {
      // Snapshot before applying drag so the whole drag is a single undo step
      this._pushHistorySnapshot();
      // Update pointsArray with the mapped (moved) points
      // CRITICAL: Get the final mapped points BEFORE clearing active points
      const mappedPoints = this._mapPointsArrayAndActivePoints();
      this.pointsArray = mappedPoints.map((points) =>
        points.map((p) => p.clone())
      );
    }

    return this._handleRegularButtonUp(event, moved, hadActivePoints);
  }

  // eslint-disable-next-line complexity
  handleSingleClick(event: MouseEvent, button: number): boolean {
    // Update hover states first to ensure we have accurate hover information
    this._updateHoveredPointFromEvent(event);
    this._updateHoveredLineFromEvent(event);

    // Vertex selection: click on an existing vertex selects/toggles it (works for all modes including MagicPolygon)
    if (button === 0 && this.hoveredPoint) {
      // For MagicPolygon mode, selection is handled in handleButtonUp to ensure shift key state is preserved
      // Skip selection handling here to avoid conflicts
      if (this.drawingMode === DrawingMode.MagicPolygon) {
        // Selection already handled in handleButtonUp, just return
        return true;
      }

      // For Polygon mode, check if clicking on the first vertex to close the polygon
      if (this.drawingMode === DrawingMode.Polygon) {
        const currentShape = this.pointsArray[this.pointsArrayIndex];
        const firstPoint = currentShape?.[0];
        // If clicking on the first vertex and we have at least 3 points, close the polygon
        if (
          firstPoint &&
          this.hoveredPoint.point.equals(firstPoint) &&
          currentShape.length >= 3
        ) {
          this._pushHistorySnapshot();
          this._closePolygons();
          this.pointsArrayIndex++;
          this.draw();
          this.onUpdate([...this.pointsArray]);
          return true;
        }
      }

      // For other modes, handle vertex selection normally
      const additive = event.shiftKey;
      this._toggleVertexSelection(this.hoveredPoint.point, additive);
      this.draw();
      return true;
    }

    // For MagicPolygon, add a point and let the hook handle the API call
    if (this.drawingMode === DrawingMode.MagicPolygon) {
      if (button === 0) {
        // Only add a new point if we're not clicking on a vertex or edge
        if (
          this.magicPolygonHandler.shouldAddSamplePoint(
            this.hoveredPoint,
            this.hoveredLine
          )
        ) {
          const cursorPosition = this._getWorldPositionFromMouseEvent(event);
          this._pushHistorySnapshot();

          // Instead of adding to pointsArray (the result polygon), add to sample points
          this.magicPolygonHandler.addSamplePoint(cursorPosition);

          this.draw();
          // Trigger update so the hook sees the new sample point
          this.onUpdate([...this.pointsArray]);
        }
        // If we're clicking on a vertex or edge, the handleButtonDown method will handle the dragging
        return true;
      }
      return true;
    }

    const currentShape = this.pointsArray[this.pointsArrayIndex];
    const lastPoint = currentShape?.[currentShape.length - 1];
    const cursorPositionRaw = this._getWorldPositionFromMouseEvent(event);
    const cursorPosition =
      lastPoint && event.shiftKey
        ? this._calculateEndPoint(lastPoint, cursorPositionRaw, true)
        : cursorPositionRaw;

    if (button === 2) {
      // Prevent context menu from opening when right-clicking to close
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();

      // Check if we're in polygon mode and can close (use flag OR check directly)
      const currentShape = this.pointsArray[this.pointsArrayIndex];
      if (
        this.drawingMode === DrawingMode.Polygon &&
        currentShape &&
        currentShape.length >= 3
      ) {
        // Close the polygon
        this._pushHistorySnapshot();
        this._updateCursorPointFromEvent(event);
        this._closePolygons();
        this.pointsArrayIndex++;
        this.draw();
        this.onUpdate([...this.pointsArray]);
        return true;
      }
    } else if (button == 0) {
      // Prevent adding new points when editing a point shape in edit mode
      if (this.initialPoints && this.drawingMode === DrawingMode.Point) {
        // Only allow moving existing points, not adding new ones
        return true;
      }

      this._pushHistorySnapshot();

      if (!this.hoveredLine && !this.hoveredPoint) {
        this._addPointToShape(cursorPosition);
      } else if (this.hoveredPoint) {
        this._addPointToShape(this.hoveredPoint.point);
      } else if (this.hoveredLine) {
        this._addPointOnLine(cursorPosition, this.hoveredLine);
      }
    }

    this._updateHoveredLineFromEvent(event);
    this._updateHoveredLineFromEvent(event);
    this.draw();
    this.onUpdate([...this.pointsArray]);
    return true;
  }

  handleDoubleClick(event: MouseEvent): boolean {
    if (this.hoveredPoint) {
      this.clear();
      this._pushHistorySnapshot();
      this._removePoint(this.hoveredPoint.point);
      this._updateHoveredPointFromEvent(event);
      this._updateHoveredLineFromEvent(event);
      this.draw();
      this.onUpdate([...this.pointsArray]);
      return true;
    }

    // Double-click closing removed - use right-click or click first vertex instead
    return false;
  }

  // eslint-disable-next-line complexity
  handleMouseMove(event: MouseEvent): boolean {
    // Handle vertex box selection drag
    if (this.vertexBoxActive && this.vertexBoxStart) {
      const x = event.clientX;
      const y = event.clientY - VIEWER_TOP_OFFSET;
      this._updateVertexBoxSelection(x, y);
      return true;
    }

    if (this.drawingMode === DrawingMode.MagicPolygon) {
      return this._handleMagicPolygonMouseMove(event);
    }

    // Allow viewer panning while previewing if right or middle button is held
    const isRightOrMiddleButtonDown =
      typeof event.buttons === 'number' &&
      ((event.buttons & 2) !== 0 || (event.buttons & 4) !== 0);

    // Handle dragging of points/edges
    if (this.activePoints.length) {
      this.activePointerCurrentPosition = this._getWorldPositionFromMouseEvent(
        event,
        false
      );
      this._updateCursorPointFromEvent(event);
      this._updateHoveredPointFromEvent(null);
      this._updateHoveredLineFromEvent(null);
      this.draw();
      return true; // Consume event when actively dragging
    }

    // Update hover states
    this._updateHoveredPointFromEvent(event);
    this._updateHoveredLineFromEvent(event);

    // Handle preview during active drawing
    if (this.pointsArray[this.pointsArrayIndex]?.length > 0) {
      this.activePointerCurrentPosition = null;
      this._updateCursorPointFromEvent(event);
      this.draw();
      // During preview, don't consume when panning (right/middle held)
      return isRightOrMiddleButtonDown ? false : true;
    }

    // For completed shapes, only consume event if hovering over interactive elements
    if (this.hoveredPoint || this.hoveredLine) {
      this._updateCursorPointFromEvent(event);
      this.draw();
      // When panning with right/middle button, allow the viewer to handle it
      return isRightOrMiddleButtonDown ? false : true;
    }

    // Not interacting - allow panning
    return false;
  }

  private _setupDraggingForMagicPolygon(event: MouseEvent): void {
    if (
      !this.activePointsMode ||
      this.activePoints.length > 0 ||
      !this.activePointerInitPosition
    ) {
      return;
    }

    // Mouse has moved - set up dragging
    this.activePointerCurrentPosition = this._getWorldPositionFromMouseEvent(
      event,
      false
    );
    if (!this.activePointerCurrentPosition) {
      return;
    }

    // Only start dragging if mouse moved more than a small *pixel* threshold
    const pxDistance =
      distanceBetweenInPx(
        this.viewer,
        this.activePointerInitPosition,
        this.activePointerCurrentPosition
      ) ?? 0;
    if (pxDistance <= 3) {
      return;
    }

    this._setActivePointsForCurrentHover();
  }

  private _setActivePointsForCurrentHover(): void {
    if (this.activePointsMode === ActiveMode.Point && this.hoveredPoint) {
      this.activePoints = [this.hoveredPoint];
    } else if (this.activePointsMode === ActiveMode.Edge && this.hoveredLine) {
      this.activePoints = [
        {
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          point: this.hoveredLine.start,
          locations: findPointLocations(
            this.pointsArray,
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            this.hoveredLine.start
          ),
        },
        {
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          point: this.hoveredLine.end,
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          locations: findPointLocations(
            this.pointsArray,
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            this.hoveredLine.end
          ),
        },
      ];
    }
  }

  private _handleMagicPolygonMouseMove(event: MouseEvent): boolean {
    const hasEditablePoints =
      this.pointsArray[this.pointsArrayIndex]?.length > 0;

    if (!hasEditablePoints) {
      // No editable points - don't consume events, allow panning
      return false;
    }

    // Check if we need to set up dragging (mouse started moving after button down)
    this._setupDraggingForMagicPolygon(event);

    // Only process mouse events if we're actively dragging points or hovering over interactive elements
    if (this.activePoints.length > 0) {
      // We're dragging points - handle the movement
      this.activePointerCurrentPosition = this._getWorldPositionFromMouseEvent(
        event,
        false
      );
      this._updateCursorPointFromEvent(event);
      this._updateHoveredPointFromEvent(null);
      this._updateHoveredLineFromEvent(null);
      this.draw();
      return true; // Consume the event
    }

    // Check if we're hovering over interactive elements
    this._updateHoveredPointFromEvent(event);
    this._updateHoveredLineFromEvent(event);

    // Only redraw if we're actually hovering over something interactive
    if (this.hoveredPoint || this.hoveredLine) {
      this._updateCursorPointFromEvent(event);
      this.draw();
      return true; // Consume the event
    }

    // Not hovering over anything interactive - don't consume the event
    return false;
  }

  private _updateHoveredPointFromEvent(event: MouseEvent | null) {
    this.hoveredPoint = null;

    if (!event) return;

    const clientX = event.clientX;
    const clientY = event.clientY - VIEWER_TOP_OFFSET;
    const cursorPoint = this.viewer.impl.intersectGround(clientX, clientY);

    const pointsToCheck = this.pointsArray.flat();

    const hoveredPoint = pointsToCheck.find((point) => {
      const distance = distanceBetweenInPx(this.viewer, cursorPoint, point);
      return distance && distance <= 15;
    });

    // Find locations in the original points array
    const locations = hoveredPoint
      ? findPointLocations(this.pointsArray, hoveredPoint)
      : [];

    if (hoveredPoint) {
      this.hoveredPoint = {
        point: hoveredPoint,
        locations,
      };
    }
  }

  private _updateHoveredLineFromEvent(event: MouseEvent | null) {
    this.hoveredLineShape?.remove();
    this.hoveredLineShape = null;
    this.hoveredLine = null;

    if (!event) return;

    const cursorPoint = this._getWorldPositionFromMouseEvent(event);
    if (!cursorPoint) return;

    const lines = this.pointsArray
      .flatMap((points) => {
        const isClosed =
          points.length > 2 && points[0].equals(points[points.length - 1]);
        return points
          .map((point, index) => {
            const nextPoint =
              points[index + 1] || (isClosed ? points[0] : undefined);
            if (!nextPoint || point.equals(nextPoint)) return null;

            // https://threejs.org/docs/#api/en/math/Line3
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            const line = new THREE.Line3(point, nextPoint);
            // https://threejs.org/docs/#api/en/math/Line3
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            const closestPoint = line.closestPointToPoint(cursorPoint, true);
            const distance = distanceBetweenInPx(
              this.viewer,
              cursorPoint,
              closestPoint
            );

            return distance !== null && distance < 5
              ? { line, distance }
              : null;
          })
          .filter(Boolean) as { line: THREE.Line3; distance: number }[];
      })
      .sort((a, b) => a.distance - b.distance);

    this.hoveredLine = lines.length > 0 ? lines[0].line : null;
  }

  private _getWorldPositionFromMouseEvent(
    event: MouseEvent,
    useSnapping = true
  ) {
    const clientX = event.clientX;
    const clientY = event.clientY - VIEWER_TOP_OFFSET;

    // Disable snapping for MagicPolygon mode
    const shouldUseSnapping =
      useSnapping && this.drawingMode !== DrawingMode.MagicPolygon;

    const snapPoint = shouldUseSnapping
      ? getSnapPoint(this.snapper, this.viewer)
      : null;
    const cursorPositionRaw = this.viewer.impl.intersectGround(
      clientX,
      clientY
    );

    return snapPoint ? snapPoint : cursorPositionRaw;
  }

  private _closePolygons() {
    this.pointsArray
      .filter((points) => points.length > 2)
      .forEach((points) => {
        const startPoint = points[0];
        const endPoint = points[points.length - 1];

        if (startPoint && endPoint && !startPoint.equals(endPoint)) {
          points.push(startPoint);
        }
      });
  }

  private _calculateEndPoint(
    previousPoint: THREE.Vector3,
    currentPoint: THREE.Vector3,
    shiftKey: boolean
  ) {
    const angle = angleBetweenTwoPoints([previousPoint, currentPoint]);
    const snappedAngle = snapAngle(angle, MEASUREMENT_TOOL_SNAPPING_ANGLE_STEP);

    if (shiftKey) {
      return endPointByAngle(
        previousPoint,
        snappedAngle,
        previousPoint.distanceTo(currentPoint)
      );
    } else {
      return currentPoint;
    }
  }

  private _mapPointsArrayAndActivePoints() {
    if (
      !this.activePointsMode ||
      !this.activePointerCurrentPosition ||
      !this.activePointerInitPosition
    ) {
      return this.pointsArray;
    }

    const movementVector = new THREE.Vector3(
      this.activePointerCurrentPosition.x - this.activePointerInitPosition.x,
      this.activePointerCurrentPosition.y - this.activePointerInitPosition.y
    );

    return this.pointsArray.map((points, shapeIndex) => {
      const totalPointsLength = points.length;
      return points
        .map((point, pointIndex) => {
          // Skip if the original point is null
          if (!point) return null;

          return this._mapPointWithActivePoints(
            point,
            pointIndex,
            shapeIndex,
            points,
            totalPointsLength,
            movementVector
          );
        })
        .filter((point): point is THREE.Vector3 => point !== null); // Filter out null points
    });
  }

  private _mapPointWithActivePoints(
    point: THREE.Vector3,
    pointIndex: number,
    shapeIndex: number,
    points: THREE.Vector3[],
    totalPointsLength: number,
    movementVector: THREE.Vector3
  ): THREE.Vector3 {
    const isMovingWithoutPreservingAngle =
      this.activePointsMode !== ActiveMode.Edge || totalPointsLength <= 2;

    const isActive = (
      shapeIndex: number,
      pointIndex: number,
      points: THREE.Vector3[]
    ) => {
      const getCanonicalIndex = (points: THREE.Vector3[], index: number) =>
        !(
          index === points.length - 1 &&
          points[0].x === points[index].x &&
          points[0].y === points[index].y
        )
          ? index
          : 0;

      const canonicalIndex = getCanonicalIndex(points, pointIndex);
      return this.activePoints.some(({ locations }) =>
        locations.some(([si, pi]) => si === shapeIndex && pi === canonicalIndex)
      );
    };

    if (!isActive(shapeIndex, pointIndex, points)) {
      return point;
    }

    if (isMovingWithoutPreservingAngle) {
      return new THREE.Vector3(
        point.x + movementVector.x,
        point.y + movementVector.y
      );
    }

    const firstIndex = 0;
    const lastIndex = points.length - 1;
    const prevIndex = (pointIndex - 1 + points.length) % points.length;
    const nextIndex = (pointIndex + 1) % points.length;
    const prev = points[prevIndex];
    const next = points[nextIndex];

    let neighbor: THREE.Vector3 | undefined = undefined;
    if (prev && !isActive(shapeIndex, prevIndex, points)) {
      neighbor = prev;
    } else if (next && !isActive(shapeIndex, nextIndex, points)) {
      neighbor = next;
    } else {
      return point; // brak sąsiada spoza aktywnych
    }

    const originalEdge = new THREE.Vector3(
      point.x - neighbor.x,
      point.y - neighbor.y
    );
    const edgeDirection = originalEdge.clone().normalize();
    const distance = movementVector.dot(edgeDirection);
    const movedPoint = new THREE.Vector3(
      point.x + edgeDirection.x * distance,
      point.y + edgeDirection.y * distance
    );

    if (pointIndex === firstIndex) {
      points[lastIndex] = movedPoint.clone();
    } else if (pointIndex === lastIndex) {
      points[firstIndex] = movedPoint.clone();
    }

    return movedPoint;
  }

  // eslint-disable-next-line complexity
  private _isShapeValid(points: THREE.Vector3[], mode: DrawingMode): boolean {
    switch (mode) {
      case DrawingMode.Point:
        return points.length === 1;
      case DrawingMode.Polyline:
        return points.length >= 2;
      case DrawingMode.Polygon:
        // For polygon, need at least 3 points and the first and last should be the same (closed)
        return (
          points.length >= 4 && points[0].equals(points[points.length - 1])
        );
      case DrawingMode.MagicPolygon:
        // Magic polygon is valid if it has at least 1 point (for single click)
        return points.length >= 1;
      case DrawingMode.Rectangle:
        // For rectangle, need exactly 5 points (4 corners + closing point)
        return (
          points.length === 5 && points[0].equals(points[points.length - 1])
        );
      case DrawingMode.DrainLine:
        // For drain lines, need at least 2 points (start and end)
        return points.length >= 2;
      case DrawingMode.RoofOutline:
        // Roof outline uses the same validation as polygon (it's just a UI mode)
        return (
          points.length >= 4 && points[0].equals(points[points.length - 1])
        );
      default:
        return false;
    }
  }

  private _createRectanglePreview(
    firstPoint: THREE.Vector3,
    cursorPoint: THREE.Vector3,
    commonArgs: [Viewer3D, Edit2D, ActiveSheetData]
  ): ISheetShape[] {
    const x1 = firstPoint.x;
    const y1 = firstPoint.y;
    const x2 = cursorPoint.x;
    const y2 = cursorPoint.y;
    const rectanglePoints = [
      firstPoint,
      new THREE.Vector3(x2, y1, 0),
      new THREE.Vector3(x2, y2, 0),
      new THREE.Vector3(x1, y2, 0),
      firstPoint,
    ];

    // Create filled rectangle preview using polygon shape
    const previewShapes = mapPointsToPolygonShape(
      [rectanglePoints],
      ...commonArgs,
      {
        default: InvalidShapeStyle,
        hovered: InvalidShapeStyle,
        selected: InvalidShapeStyle,
      }
    );

    return previewShapes;
  }

  private _createPolylineShapes(
    pointsWithCursor: THREE.Vector3[][],
    pointsArrayIndex: number,
    commonArgs: [Viewer3D, Edit2D, ActiveSheetData]
  ): ISheetShape[] {
    // For editing mode (when we have initial points), check validity of all existing shapes
    // For drawing mode, check validity of the current shape being drawn
    let isValid = true;

    if (this.initialPoints) {
      // Editing mode: check if all existing shapes are valid
      isValid = this.pointsArray.every((points) =>
        this._isShapeValid(points, this.drawingMode)
      );
    } else {
      // Drawing mode: check validity of the current shape being drawn
      const actualPoints = this.pointsArray[pointsArrayIndex] || [];
      const lastCompletedPoints =
        pointsArrayIndex > 0 ? this.pointsArray[pointsArrayIndex - 1] : [];
      const pointsToCheck =
        actualPoints.length > 0 ? actualPoints : lastCompletedPoints;
      isValid = this._isShapeValid(pointsToCheck, this.drawingMode);
    }

    const lineStyle = isValid ? ValidLineStyle : InvalidLineStyle;
    return mapPointsToPolylineShape(pointsWithCursor, ...commonArgs, {
      default: lineStyle,
      hovered: lineStyle,
      selected: lineStyle,
    });
  }

  private _createPolygonShapes(
    pointsWithCursor: THREE.Vector3[][],
    pointsArrayIndex: number,
    commonArgs: [Viewer3D, Edit2D, ActiveSheetData]
  ): ISheetShape[] {
    // For editing mode (when we have initial points), check validity of all existing shapes
    // For drawing mode, check validity of the current shape being drawn
    let isValid = true;

    if (this.initialPoints) {
      // Editing mode: check if all existing shapes are valid
      isValid = this.pointsArray.every((points) =>
        this._isShapeValid(points, this.drawingMode)
      );
    } else {
      // Drawing mode: check validity of the current shape being drawn
      const actualPoints = this.pointsArray[pointsArrayIndex] || [];
      const lastCompletedPoints =
        pointsArrayIndex > 0 ? this.pointsArray[pointsArrayIndex - 1] : [];
      const pointsToCheck =
        actualPoints.length > 0 ? actualPoints : lastCompletedPoints;
      isValid = this._isShapeValid(pointsToCheck, this.drawingMode);
    }

    const shapeStyle = isValid ? ValidShapeStyle : InvalidShapeStyle;

    const shapes = mapPointsToPolygonShape(pointsWithCursor, ...commonArgs, {
      default: shapeStyle,
      hovered: shapeStyle,
      selected: shapeStyle,
    });

    return shapes;
  }

  private _createSplitLineShapes(
    pointsWithCursor: THREE.Vector3[][],
    pointsArrayIndex: number,
    commonArgs: [Viewer3D, Edit2D, ActiveSheetData]
  ): ISheetShape[] {
    // For drain lines, create split line shapes with dashed style
    let isValid = true;

    if (this.initialPoints) {
      // Editing mode: check if all existing shapes are valid
      isValid = this.pointsArray.every((points) =>
        this._isShapeValid(points, this.drawingMode)
      );
    } else {
      // Drawing mode: check validity of the current shape being drawn
      const actualPoints = this.pointsArray[pointsArrayIndex] || [];
      const lastCompletedPoints =
        pointsArrayIndex > 0 ? this.pointsArray[pointsArrayIndex - 1] : [];
      const pointsToCheck =
        actualPoints.length > 0 ? actualPoints : lastCompletedPoints;
      isValid = this._isShapeValid(pointsToCheck, this.drawingMode);
    }

    const lineStyle = isValid ? ValidLineStyle : InvalidLineStyle;

    // Create drain line-specific style with dashed lines
    const drainLineStyle = {
      ...lineStyle,
      lineColor: 'blue',
      lineWidth: 2,
      lineStyle: 3, // DashedLarge
    };

    return mapPointsToSplitLineShape(pointsWithCursor, ...commonArgs, {
      default: drainLineStyle,
      hovered: drainLineStyle,
      selected: drainLineStyle,
    });
  }

  private _renderVertices(
    commonArgs: [Viewer3D, Edit2D, ActiveSheetData],
    mappedPointsArray: THREE.Vector3[][]
  ): void {
    // Clear existing screen-space vertices
    this.screenSpaceVertices.forEach((vertex) => vertex.remove());
    this.screenSpaceVertices = [];

    // Render all vertices for all shapes (current and completed)
    mappedPointsArray.forEach((points, shapeIndex) => {
      // Skip if points array is null, undefined, or empty
      if (!points || points.length === 0) {
        return;
      }

      const isPreviewShape =
        this.drawingMode === DrawingMode.Rectangle &&
        this.pointsArray[this.pointsArrayIndex]?.length === 1 &&
        this.cursorPoint &&
        shapeIndex === mappedPointsArray.length - 1;

      if (this.drawingMode === DrawingMode.Rectangle && points.length === 5) {
        // For rectangles, only show the 4 corner vertices (skip the closing point)
        const cornerPoints = points.slice(0, 4);
        cornerPoints.forEach((point, pointIndex) => {
          // Skip if point is null or undefined
          if (!point) {
            return;
          }

          // Create screen-space vertex with a getter that references the mapped points array
          const isFirstVertex = pointIndex === 0;
          const baseStyle = isPreviewShape
            ? VertexStyle
            : isFirstVertex
            ? FirstVertexStyle
            : VertexStyle;
          const isSelected = this._isPointSelected(point);
          const style = isSelected ? FirstVertexStyle : baseStyle;

          try {
            const vertex = new ScreenSpaceVertex(
              this.viewer,
              () => mappedPointsArray[shapeIndex][pointIndex],
              style,
              isFirstVertex
            );
            this.screenSpaceVertices.push(vertex);
          } catch (error) {
            console.error(
              `_renderVertices: Failed to create ScreenSpaceVertex for rectangle point ${pointIndex}`,
              { point, error }
            );
          }
        });
      } else {
        // For other shapes, show all vertices
        points.forEach((point, pointIndex) => {
          // Skip if point is null or undefined
          if (!point) {
            return;
          }

          // Create screen-space vertex with a getter that references the mapped points array
          const isFirstVertex = pointIndex === 0;
          const baseStyle = isFirstVertex ? FirstVertexStyle : VertexStyle;
          const isSelected = this._isPointSelected(point);
          const style = isSelected ? FirstVertexStyle : baseStyle;

          try {
            const vertex = new ScreenSpaceVertex(
              this.viewer,
              () => mappedPointsArray[shapeIndex][pointIndex],
              style,
              isFirstVertex
            );
            this.screenSpaceVertices.push(vertex);
          } catch (error) {
            console.error(
              `_renderVertices: Failed to create ScreenSpaceVertex for regular point ${pointIndex}`,
              { point, error }
            );
          }
        });
      }
    });
  }

  // Add method to force vertex updates when points change
  private _updateVertices(): void {
    // Force update all screen-space vertices
    this.screenSpaceVertices.forEach((vertex) => vertex.update());
  }

  getResults(): {
    mode: DrawingMode;
    points: THREE.Vector3[][];
  } {
    return {
      mode: this.drawingMode,
      points: this.pointsArray,
    };
  }

  // Helper to get sample points
  getSamplePoints(): THREE.Vector3[] {
    return this.magicPolygonHandler.getSamplePoints();
  }

  // Set loading state for magic polygon
  setMagicPolygonLoading(loading: boolean): void {
    this.isMagicPolygonLoading = loading;
  }

  updateMagicPolygonResults(results: any) {
    if (this.drawingMode === DrawingMode.MagicPolygon) {
      // Snapshot before applying new MagicPolygon results so undo restores pre-result state
      this._pushHistorySnapshot();

      this.magicPolygonHandler.updateResults(results);

      // Convert the API result into editable points
      if (results.valid && results.multipolygon) {
        const editablePoints = this.magicPolygonHandler.convertToEditablePoints(
          results.multipolygon,
          this.viewer,
          this.activeSheetData
        );

        if (editablePoints.length > 0) {
          this.pointsArray = [editablePoints];
          this.pointsArrayIndex = 0;
        }
      }

      this.draw(); // Redraw to show the new results
    }
  }

  /**
   * Get the current edited multipolygon for MagicPolygon mode
   * This allows the hook to access the edited polygon data for submission
   */
  getCurrentEditedMagicPolygon(): any {
    if (this.drawingMode !== DrawingMode.MagicPolygon) {
      return null;
    }

    // If we have edited points, convert them to multipolygon format
    if (this.magicPolygonHandler.hasEditedPoints(this.pointsArray)) {
      return this.magicPolygonHandler.convertEditedPointsToMultipolygon(
        this.pointsArray,
        this.viewer,
        this.activeSheetData
      );
    }

    return null;
  }

  private _renderPolygon() {
    // Clear existing shapes
    this.clear();

    const commonArgs: [Viewer3D, Edit2D, ActiveSheetData] = [
      this.viewer,
      this.edit2d,
      this.activeSheetData,
    ];

    // Map points with active points for real-time dragging
    const mappedPointsArray = this._mapPointsArrayAndActivePoints();

    // Create polygon shapes
    this.shapes = this._createPolygonShapes(
      mappedPointsArray,
      this.pointsArrayIndex,
      commonArgs
    );

    // Draw the shapes to make them visible
    this.shapes.forEach((shape) => {
      shape.setSelected(true);
      shape.draw();
    });

    // Render vertices for interactivity
    this._renderVertices(commonArgs, mappedPointsArray);
  }

  // eslint-disable-next-line complexity
  draw() {
    // Clear existing shapes
    this.clear();

    const commonArgs: [Viewer3D, Edit2D, ActiveSheetData] = [
      this.viewer,
      this.edit2d,
      this.activeSheetData,
    ];

    // Map points with active points for real-time dragging
    const mappedPointsArray = this._mapPointsArrayAndActivePoints();

    // Create pointsWithCursor array that includes cursor point for live previews
    const pointsWithCursor = mappedPointsArray.map((points, index) => {
      if (index === this.pointsArrayIndex && this.cursorPoint) {
        return [...points, this.cursorPoint];
      }
      return points;
    });

    // Create shapes based on drawing mode
    switch (this.drawingMode) {
      case DrawingMode.Point:
        this.shapes = this._createPolylineShapes(
          pointsWithCursor,
          this.pointsArrayIndex,
          commonArgs
        );
        break;
      case DrawingMode.Polyline:
        this.shapes = this._createPolylineShapes(
          pointsWithCursor,
          this.pointsArrayIndex,
          commonArgs
        );
        break;
      case DrawingMode.Polygon:
      case DrawingMode.RoofOutline:
        this.shapes = this._createPolygonShapes(
          pointsWithCursor,
          this.pointsArrayIndex,
          commonArgs
        );
        break;
      case DrawingMode.MagicPolygon:
        // For MagicPolygon, render the API results as Edit2D shapes
        if (this.magicPolygonHandler.hasValidResults()) {
          // If we have editable points, render them as a regular polygon for interactivity
          if (this.pointsArray[this.pointsArrayIndex]?.length > 0) {
            this._renderPolygon();
            // Return early to prevent the main draw() method from continuing
            // since _renderPolygon() already handles shape creation and drawing
            return;
          } else {
            // Otherwise, render the API results as static shapes
            const results = this.magicPolygonHandler.getResults();
            if (results?.multipolygon) {
              this.shapes = this.magicPolygonHandler.createShapes(
                results.multipolygon,
                commonArgs
              );
              // The MagicPolygonHandler.createShapes() method already calls draw() on the shapes
              // so we don't need to call draw() again in the main draw() method
              // Just return early to prevent the main draw() method from continuing
              return;
            }
          }
        }
        // If we have points (carried over or clicked) but no API results, still render as a polygon
        if (this.pointsArray[this.pointsArrayIndex]?.length > 0) {
          this._renderPolygon();
          return;
        }
        // If we're waiting for API results, don't render anything (just show loading cursor)
        break;
      case DrawingMode.Rectangle:
        if (
          this.pointsArray[this.pointsArrayIndex]?.length === 1 &&
          this.cursorPoint
        ) {
          // Create a combined array with completed rectangles and preview
          const completedRectangles = this.pointsArray
            .slice(0, this.pointsArrayIndex)
            .filter((points) => points.length > 0);

          // Create preview rectangle points
          const firstPoint = this.pointsArray[this.pointsArrayIndex][0];
          const x1 = firstPoint.x;
          const y1 = firstPoint.y;
          const x2 = this.cursorPoint.x;
          const y2 = this.cursorPoint.y;
          const previewRectanglePoints = [
            firstPoint,
            new THREE.Vector3(x2, y1, 0),
            new THREE.Vector3(x2, y2, 0),
            new THREE.Vector3(x1, y2, 0),
            firstPoint,
          ];

          // Combine completed rectangles with preview for vertex rendering
          const combinedPointsArray = [
            ...completedRectangles,
            previewRectanglePoints,
          ];

          // Show completed rectangles
          if (completedRectangles.length > 0) {
            const completedShapes = this._createPolygonShapes(
              completedRectangles,
              this.pointsArrayIndex - 1,
              commonArgs
            );
            this.shapes.push(...completedShapes);
          }

          // Show rectangle preview after first click
          const previewShapes = this._createRectanglePreview(
            firstPoint,
            this.cursorPoint,
            commonArgs
          );
          this.shapes.push(...previewShapes);

          // Render vertices for all shapes (completed + preview)
          this._renderVertices(commonArgs, combinedPointsArray);
        } else {
          // Show completed rectangles
          this.shapes = this._createPolygonShapes(
            mappedPointsArray,
            this.pointsArrayIndex,
            commonArgs
          );
        }
        break;
      case DrawingMode.DrainLine:
        // For drain lines, create split line shapes
        this.shapes = this._createSplitLineShapes(
          pointsWithCursor,
          this.pointsArrayIndex,
          commonArgs
        );
        break;
    }

    this.shapes.forEach((shape) => {
      shape.setSelected(true);
      shape.draw();
    });

    // Render all vertices (clicked points) for the current shape using mapped points
    // Note: For MagicPolygon, vertices are already rendered above
    if (this.drawingMode !== DrawingMode.MagicPolygon) {
      this._renderVertices(commonArgs, mappedPointsArray);
    }

    // Force update vertices to ensure they reflect the current point positions
    this._updateVertices();

    // Render hovered line if any
    if (this.hoveredLine) {
      // https://threejs.org/docs/#api/en/math/Line3
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      const points = [this.hoveredLine.start, this.hoveredLine.end];
      this.hoveredLineShape = new PolylineShape(
        ...commonArgs,
        undefined,
        points,
        {
          default: HoveredValidLineStyle,
          hovered: HoveredValidLineStyle,
          selected: HoveredValidLineStyle,
        }
      );
      this.hoveredLineShape.setHovered(true);
      this.hoveredLineShape.draw();
    }

    // Force layer update to ensure shapes are visible
    if (this.edit2d.defaultContext && this.edit2d.defaultContext.layer) {
      const layer = this.edit2d.defaultContext.layer;
      if (typeof (layer as any).update === 'function') {
        (layer as any).update();
      }
    }
  }

  clear() {
    this.shapes.forEach((shape) => shape.remove());
    this.shapes = [];
    this.hoveredLineShape?.remove();
    this.hoveredLineShape = null;

    // Clear screen-space vertices
    this.screenSpaceVertices.forEach((vertex) => vertex.remove());
    this.screenSpaceVertices = [];
  }

  private _removePoint(toRemove: THREE.Vector3) {
    this.pointsArray = this.pointsArray
      .map((points) => points.filter((point) => !point.equals(toRemove)))
      .filter((points) => points.length);
    this.pointsArrayIndex = this.pointsArray.length;
  }

  // eslint-disable-next-line complexity
  private _addPointToShape(point: THREE.Vector3) {
    if (!this.pointsArray[this.pointsArrayIndex])
      this.pointsArray[this.pointsArrayIndex] = [];

    const firstPoint = this.pointsArray[this.pointsArrayIndex][0];
    const distance = distanceBetweenInPx(this.viewer, point, firstPoint);
    const snapped = getSnapPointByDistance(point, firstPoint, distance);

    switch (this.drawingMode) {
      case DrawingMode.Point:
        this.pointsArray[this.pointsArrayIndex].push(point);
        this.pointsArrayIndex++;
        break;
      case DrawingMode.Polyline:
      case DrawingMode.Polygon:
      case DrawingMode.MagicPolygon:
        this.pointsArray[this.pointsArrayIndex].push(snapped);
        if (firstPoint?.equals(snapped)) this.pointsArrayIndex++;
        break;
      case DrawingMode.Rectangle:
        if (this.pointsArray[this.pointsArrayIndex].length === 0) {
          // First point of rectangle
          this.pointsArray[this.pointsArrayIndex].push(snapped);
        } else if (this.pointsArray[this.pointsArrayIndex].length === 1) {
          // Second point of rectangle - create the full rectangle
          const firstCorner = this.pointsArray[this.pointsArrayIndex][0];
          const secondCorner = snapped;

          // Create rectangle points in clockwise order
          const x1 = firstCorner.x;
          const y1 = firstCorner.y;
          const x2 = secondCorner.x;
          const y2 = secondCorner.y;

          // Add the remaining corners to complete the rectangle
          this.pointsArray[this.pointsArrayIndex].push(
            new THREE.Vector3(x2, y1, 0)
          ); // Top right
          this.pointsArray[this.pointsArrayIndex].push(secondCorner); // Bottom right
          this.pointsArray[this.pointsArrayIndex].push(
            new THREE.Vector3(x1, y2, 0)
          ); // Bottom left
          this.pointsArray[this.pointsArrayIndex].push(firstCorner); // Close the rectangle

          this.pointsArrayIndex++;
        }
        break;
      case DrawingMode.DrainLine:
        if (this.pointsArray[this.pointsArrayIndex].length === 0) {
          // First point of drain line
          this.pointsArray[this.pointsArrayIndex].push(snapped);
        } else if (this.pointsArray[this.pointsArrayIndex].length === 1) {
          // Second point of drain line - create the full line
          const endPoint = snapped;

          // Add the end point to complete the line
          this.pointsArray[this.pointsArrayIndex].push(endPoint);

          this.pointsArrayIndex++;
        }
        break;
    }
  }

  private _addPointOnLine(point: THREE.Vector3, line: THREE.Line3) {
    // https://threejs.org/docs/#api/en/math/Line3
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const pointOnLine = line.closestPointToPoint(point, true);
    // https://threejs.org/docs/#api/en/math/Line3
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const { start, end } = line;

    this.pointsArray = this.pointsArray.map((points) => {
      const startIndex = points.findIndex((p) => p.equals(start));
      const endIndex = points.findLastIndex((p) => p.equals(end));

      if (startIndex === -1 || endIndex === -1) return points;

      const insertIndex = Math.min(startIndex, endIndex) + 1;
      const updated = [...points];

      updated.splice(insertIndex, 0, pointOnLine.clone());

      return updated;
    });
  }

  private _shouldShowCursorPoint(): boolean {
    // In edit mode, only show cursor point for polylines or when creating new shapes
    if (this.initialPoints && this.drawingMode !== DrawingMode.Polyline) {
      // For point drawing mode in edit mode, never show cursor point
      if (this.drawingMode === DrawingMode.Point) {
        return false;
      }

      // Check if we're currently creating a new shape (pointsArrayIndex points to a new shape)
      const currentShape = this.pointsArray[this.pointsArrayIndex];
      if (!currentShape || currentShape.length === 0) {
        // Not creating a new shape, so don't show cursor point
        return false;
      }
    }
    return true;
  }

  private _updateCursorPointFromEvent(event: MouseEvent) {
    // For MagicPolygon, don't show cursor point at all since it's not needed
    if (this.drawingMode === DrawingMode.MagicPolygon) {
      this.cursorPoint = null;
      return;
    }

    if (!this._shouldShowCursorPoint()) {
      this.cursorPoint = null;
      return;
    }

    // Only show cursor point if we're actively drawing a new shape
    // (i.e., the current shape index has points and we're not just editing existing shapes)
    const currentShape = this.pointsArray[this.pointsArrayIndex];
    if (!currentShape || currentShape.length === 0) {
      this.cursorPoint = null;
      return;
    }

    // Use the snapped position for cursor preview
    const point = this._getWorldPositionFromMouseEvent(event);
    if (!point) {
      this.cursorPoint = null;
      return;
    }

    const firstPoint = currentShape[0];
    const lastPoint = currentShape[currentShape.length - 1];

    if (event.shiftKey && lastPoint) {
      this.cursorPoint = this._calculateEndPoint(lastPoint, point, true);
    } else if (this.hoveredPoint) {
      this.cursorPoint = this.hoveredPoint.point;
    } else if (firstPoint) {
      const distance = distanceBetweenInPx(this.viewer, point, firstPoint);
      this.cursorPoint = getSnapPointByDistance(point, firstPoint, distance);
    } else {
      this.cursorPoint = point;
    }
  }
}
